/**
 * Calculation engine client — the financial-advice calculation engine now runs server-side
 * (server/src/rest/calculation/) rather than in the browser. One thin wrapper per REST entry
 * point, matching the original local function's parameter order 1:1 so call sites only need to
 * add `await calculationApi.` in front of the existing call (see the calculation-engine migration
 * plan for the exceptions — a handful of functions that used to mutate their arguments in place
 * now return the mutated object(s) instead, since that's the only way a REST call can "mutate").
 */
import axios from 'axios';
import { getAccessToken } from './authClient.ts';
import { normalizeNumericFactFindFields, normalizeAssetItem, normalizeAssetsMap } from '../utils/normalizeNumericFields.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';
import type { FactFindAsset, FactFindAssetMap, FactFindGiftsHistory } from '../../../shared/src/calculation/factFindTypes.ts';
import { activeFirmAssumptions } from '../../../shared/src/calculation/investmentProfiles.ts';
import type { AgedCareCosts, AgedCareFeeSchedule } from '../../../shared/src/calculation/agedCareCostTypes.ts';
import { apiBaseUrl } from './apiBase.ts';

type DraftAsset = FactFindAsset;
type DraftAssetMap = FactFindAssetMap;

/**
 * Ephemeral calculation-time bags with no shared type (see shared/src/calculation/factFindTypes.ts's
 * own doc comment: `strategyOptions`/`projectionContext` shapes are scattered across ~15 strategy
 * modules — modelling them is a separate effort). The calc engine's response shape (`financials` and
 * every other `postCalc` return value) is likewise untyped here for the same reason — it's whatever
 * the corresponding server/src/rest/calculation/ route returns.
 */
type StrategyOptions = Record<string, unknown>;
type ProjectionContext = Record<string, unknown>;
type Financials = Record<string, unknown>;

function getApiBaseUrl(): string {
  // One source for this — see services/apiBase.ts for why eight copies was a problem.
  return apiBaseUrl();
}

async function authHeaders(): Promise<{ Authorization: string; 'Content-Type': string }> {
  const token = await getAccessToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// Request-body keys that carry a whole fact-find-shaped object (`factFind` — the wire name the routes
// still read, whatever the local is called — or an equivalent clone, e.g. getGrannyFlatBeforeAfter's
// `baseFactFind`, applyResidualDeployment's `data`) vs. a single asset row vs. the whole assets map.
// The calc engine's Money fields are typed `number` — this is the single choke point every
// calculation-engine call passes through, so numeric-input strings are coerced here rather than at
// each of the ~50 call sites across the app.
const FACTFIND_SHAPED_KEYS = ['factFind', 'baseFactFind', 'baselineFactFind', 'simulatedFactFind', 'data'];
const ASSET_ITEM_KEYS = ['asset', 'propertyAsset'];
const ASSETS_MAP_KEYS = ['assets'];

function normalizeCalcRequestBody(body: Record<string, unknown> | undefined): Record<string, unknown> {
  // Loosely typed on purpose: this is the choke point every calc-engine call passes through (~50
  // call sites), carrying whatever mix of factFind/asset/strategyOptions shapes each route needs.
  const clone: Record<string, any> = structuredClone(body ?? {});
  // The signed-in firm's capital market assumptions ride with every fact-find-shaped payload, so
  // the server engine prices at the SAME table this browser session displays — the one rule this
  // whole system rests on: the numbers shown must be the numbers used. Refreshed per request from
  // the firm's current settings; absent for consumer/default sessions.
  const firmCma = activeFirmAssumptions();
  for (const key of FACTFIND_SHAPED_KEYS) {
    if (clone[key]) {
      normalizeNumericFactFindFields(clone[key]);
      if (firmCma) clone[key].firmAssumptions = firmCma;
    }
  }
  for (const key of ASSET_ITEM_KEYS) {
    if (clone[key]) normalizeAssetItem(clone[key]);
  }
  for (const key of ASSETS_MAP_KEYS) {
    if (clone[key]) normalizeAssetsMap(clone[key]);
  }
  return clone;
}

/**
 * How long a COMPOSITE suggestion-engine call may take. The ordinary 30s suits the fine-grained
 * calls (one assessment, one projection); it is the wrong budget for an evaluation that projects
 * every candidate a generator produced. Measured at ~100ms per candidate per run, so the aged care
 * engine — which can generate 300+ scenarios for a client with several asset types (19 RAD levels
 * × funding-order permutations × keep/rent × downsize variants, plus debt-payoff twins) — crossed
 * 30s and failed outright with "timeout of 30000ms exceeded", after the adviser had waited the
 * full 30 seconds. Correctness is not negotiable, so the answer is to allow the time rather than
 * to evaluate fewer candidates.
 */
const COMPOSITE_ENGINE_TIMEOUT_MS = 180_000;

async function postCalc(name: string, body: Record<string, unknown>, timeoutMs = 30_000): Promise<any> {
  const headers = await authHeaders();
  try {
    const res = await axios.post(`${getApiBaseUrl()}/rest/calculation/${name}`, normalizeCalcRequestBody(body), { headers, timeout: timeoutMs });
    return res.data.result;
  } catch (err) {
    // "timeout of 30000ms exceeded" tells an adviser nothing they can act on.
    if (axios.isAxiosError(err) && err.code === 'ECONNABORTED') {
      throw new Error(
        'This analysis is taking longer than we allow for. It usually means there are a lot of strategies to model for this client — try shortening the projection period, then run it again.',
      );
    }
    throw err;
  }
}

// ── Core financials / aged care fees ────────────────────────────────────────────────────────
export const calculateFinancials = (
  factFind: DraftFactFind,
  strategyOptions: StrategyOptions = {},
  yearsSinceRented = 0,
  projectionContext: ProjectionContext = {},
) => postCalc('calculateFinancials', { factFind, strategyOptions, yearsSinceRented, projectionContext });

export const calculateDeprivedAssets = (
  giftsHistory: FactFindGiftsHistory,
  strategyOptions: StrategyOptions = {},
  asOfDate: Date = new Date(),
  extraGiftsByFyEnd: Record<string, number> | null = null,
) => postCalc('calculateDeprivedAssets', { giftsHistory, strategyOptions, asOfDate, extraGiftsByFyEnd });

export const computeMeansTestedFees = (agedCareAssessableIncome: number, agedCareAssessableAssets: number, useScheduleCoupleIncomeBands: boolean) =>
  postCalc('computeMeansTestedFees', { agedCareAssessableIncome, agedCareAssessableAssets, useScheduleCoupleIncomeBands });

export const getMtaBreakdown = (agedCareAssessableIncome: number, agedCareAssessableAssets: number, useScheduleCoupleIncomeBands: boolean) =>
  postCalc('getMtaBreakdown', { agedCareAssessableIncome, agedCareAssessableAssets, useScheduleCoupleIncomeBands });

/** The 1 July 2014 fee arrangements' means-tested care fee (no-worse-off residents) from the same assessable means. */
export const computeMeansTestedCareFee2014 = (agedCareAssessableIncome: number, agedCareAssessableAssets: number, useScheduleCoupleIncomeBands: boolean) =>
  postCalc('computeMeansTestedCareFee2014', { agedCareAssessableIncome, agedCareAssessableAssets, useScheduleCoupleIncomeBands });

interface CalculateAgedCareCostsParams {
  factFind: DraftFactFind;
  financials: Financials;
  radInput: number;
  radPaid: number;
  // Every call site passes the manual-control `overallStrategy` bag (rentHome/downsizeHome/etc.),
  // not a string — see StrategyOptions above (ephemeral, ~15 strategy modules, out of scope to model).
  overallStrategy: Record<string, unknown>;
  extraServiceFeeInput?: number;
  formatCurrency?: boolean;
  /** Whose fees, where a couple's two residents are priced separately; and the pair's total deposit. */
  resident?: 'client' | 'partner';
  radPaidHousehold?: number;
}

// formatCurrency: pass `true` to request calculationBreakdowns (tooltip explanations) formatted
// server-side — an actual function can't cross the network, so this can't be the client's formatCurrency.
export const calculateAgedCareCosts = ({ factFind, financials, radInput, radPaid, overallStrategy, extraServiceFeeInput = 0, formatCurrency = false, resident, radPaidHousehold }: CalculateAgedCareCostsParams) =>
  postCalc('calculateAgedCareCosts', { factFind, financials, radInput, radPaid, overallStrategy, extraServiceFeeInput, formatCurrency: !!formatCurrency, ...(resident ? { resident } : {}), ...(radPaidHousehold != null ? { radPaidHousehold } : {}) });

export const isIllnessSeparatedCouple = (factFind: DraftFactFind, strategyOptions: StrategyOptions = {}) =>
  postCalc('isIllnessSeparatedCouple', { factFind, strategyOptions });

export const getAgedCareModellerResidencyOverrides = (factFind: DraftFactFind) =>
  postCalc('getAgedCareModellerResidencyOverrides', { factFind });

export const calculateAge = (dob: string | Date, refDate: Date = new Date()) => postCalc('calculateAge', { dob, refDate });

export const resolveMtaCoupleIncomeThresholds = (factFind: DraftFactFind, strategyOptions: StrategyOptions = {}) =>
  postCalc('resolveMtaCoupleIncomeThresholds', { factFind, strategyOptions });

// ── Multi-year projections ──────────────────────────────────────────────────────────────────
export const runProjections = (
  factFind: DraftFactFind,
  strategyOptions: StrategyOptions,
  agedCareCosts: AgedCareFeeSchedule,
  legacyYears: number,
  options: Record<string, unknown> = {},
  /*
   * A MULTI-YEAR RUN IS A COMPOSITE CALL TOO (owner-caught, 25 September 2026: "the suggestion
   * engine came up with an error message"). Every other engine call already allowed 180 seconds;
   * this one was left on the 30-second default even though it is the heaviest single request the
   * app makes — every year, every asset, every plugin. A long projection period on a client with
   * several asset types crossed 30 seconds and failed outright, which reads to an adviser as the
   * tool breaking rather than as a limit being hit. The projection period is editable on the
   * walk-in tiles now, so the reader can ask for 40 years in one keystroke.
   */
) => postCalc('runProjections', { factFind, strategyOptions, agedCareCosts, legacyYears, options }, COMPOSITE_ENGINE_TIMEOUT_MS);

// ── Cashflow & Age Pension (no care event) ──────────────────────────────────────────────────
export const evaluateCashflowPensionCandidates = (
  factFind: DraftFactFind,
  candidates: unknown[],
  legacyYears: number,
  supportCostAnnual = 0,
  // An engine call like the other four: it evaluates every candidate, so it gets the same allowance.
) => postCalc('evaluateCashflowPensionCandidates', { factFind, candidates, legacyYears, supportCostAnnual }, COMPOSITE_ENGINE_TIMEOUT_MS);

export const computeYearOneFullAustralianFyBlend = (params: Record<string, unknown>) => postCalc('computeYearOneFullAustralianFyBlend', params);

export const computeYearOneFullAustralianFyBlendSupportAtHome = (params: Record<string, unknown>) =>
  postCalc('computeYearOneFullAustralianFyBlendSupportAtHome', params);

export const computeYearOneFullAustralianFyBlendRetirementVillage = (params: Record<string, unknown>) =>
  postCalc('computeYearOneFullAustralianFyBlendRetirementVillage', params);

// ── Retirement Village ──────────────────────────────────────────────────────────────────────
export const runRetirementVillageProjections = (
  factFind: DraftFactFind,
  strategyOptions: StrategyOptions,
  villageInputs: Record<string, unknown>,
  legacyYears: number,
  options: Record<string, unknown> = {},
  // The village's own multi-year run, for the same reason as runProjections above.
) => postCalc('runRetirementVillageProjections', { factFind, strategyOptions, villageInputs, legacyYears, options }, COMPOSITE_ENGINE_TIMEOUT_MS);

export const getVillageCostSummary = (villageInputs: Record<string, unknown>) => postCalc('getVillageCostSummary', { villageInputs });

export const buildSimulatedFactFindForVillageStrategy = (factFind: DraftFactFind, rvStrategy: Record<string, unknown>, villageInputs: Record<string, unknown>) =>
  postCalc('buildSimulatedFactFindForVillageStrategy', { factFind, rvStrategy, villageInputs });

export const computeRvIpNetForAllocation = (factFind: DraftFactFind, propertyAsset: DraftAsset, grossAmount: number) =>
  postCalc('computeRvIpNetForAllocation', { factFind, propertyAsset, grossAmount });

export const computeRvEntryFeeFundingTotals = (factFind: DraftFactFind, entryFeeFunding: Record<string, unknown> = {}, cgtOverrides: Record<string, unknown> = {}) =>
  postCalc('computeRvEntryFeeFundingTotals', { factFind, entryFeeFunding, cgtOverrides });

export const computeInvestmentPropertyNetForAmount = (factFind: DraftFactFind, propertyAsset: DraftAsset, amountToSell: number) =>
  postCalc('computeInvestmentPropertyNetForAmount', { factFind, propertyAsset, amountToSell });

// ── Annuities ────────────────────────────────────────────────────────────────────────────────
export const buildAnnuityImpact = (factFind: DraftFactFind, strategy: Record<string, unknown>) => postCalc('buildAnnuityImpact', { factFind, strategy });

export const applyAnnuityToFactFind = (factFind: DraftFactFind, annuityRecord: Record<string, unknown>) =>
  postCalc('applyAnnuityToFactFind', { factFind, annuityRecord });

export const summariseAnnuityImpact = (annuityRecord: Record<string, unknown>) => postCalc('summariseAnnuityImpact', { annuityRecord });

// ── Support at Home ─────────────────────────────────────────────────────────────────────────
export const calculateSupportAtHomeContribution = (
  factFind: DraftFactFind,
  financials: Financials,
  participantType = 'standard',
  nwopCategory: string | null = null,
  meansDisclosed = true,
  previousITCF = 0,
  person: 'client' | 'partner' = 'client',
  contributionOptions: Record<string, unknown> = {},
) =>
  postCalc('calculateSupportAtHomeContribution', {
    factFind,
    financials,
    participantType,
    nwopCategory,
    meansDisclosed,
    previousITCF,
    person,
    contributionOptions,
  });

export const calculateIndividualContributions = (
  factFind: DraftFactFind,
  financials: Financials,
  participantType = 'standard',
  nwopCategory: string | null = null,
  meansDisclosed = true,
  previousITCF = 0,
  incomeSplitRatio = 0.5,
  contributionOptions: Record<string, unknown> = {},
) =>
  postCalc('calculateIndividualContributions', {
    factFind,
    financials,
    participantType,
    nwopCategory,
    meansDisclosed,
    previousITCF,
    incomeSplitRatio,
    contributionOptions,
  });

export const yearsUntilCapReached = (annualContribution: number, currentCumulative = 0, participantType = 'standard') =>
  postCalc('yearsUntilCapReached', { annualContribution, currentCumulative, participantType });

// ── CGT / disposal tax ──────────────────────────────────────────────────────────────────────
export const calculateSharePartialDisposalTax = (factFind: DraftFactFind, asset: DraftAsset, saleAmount: number, details: Record<string, unknown> = {}) =>
  postCalc('calculateSharePartialDisposalTax', { factFind, asset, saleAmount, details });

export const getEffectiveDisposalCgt = (taxSummary: Record<string, unknown>, overrideTotal: number | string | null | undefined) =>
  postCalc('getEffectiveDisposalCgt', { taxSummary, overrideTotal });

export const parseCgtOverride = (raw: unknown) => postCalc('parseCgtOverride', { raw });

export const getInvestmentPropertyTaxSummary = (factFind: DraftFactFind, investmentPropertyDetails: Record<string, unknown>) =>
  postCalc('getInvestmentPropertyTaxSummary', { factFind, investmentPropertyDetails });

export const calculateInvestmentPropertyDisposalTax = (factFind: DraftFactFind, asset: DraftAsset, details: Record<string, unknown> = {}) =>
  postCalc('calculateInvestmentPropertyDisposalTax', { factFind, asset, details });

export const resolveCostBase = (asset: DraftAsset, config: Record<string, unknown>) => postCalc('resolveCostBase', { asset, config });

export const isPreCgtAsset = (asset: DraftAsset) => postCalc('isPreCgtAsset', { asset });

interface AssessableGainForSaleParams {
  costBase: number;
  proceeds: number;
  acquisitionDate: string;
  disposalDate?: Date;
  cpiRate?: number;
  config: Record<string, unknown>;
}

export const assessableGainForSale = ({ costBase, proceeds, acquisitionDate, disposalDate = new Date(), cpiRate = 0, config }: AssessableGainForSaleParams) =>
  postCalc('assessableGainForSale', { costBase, proceeds, acquisitionDate, disposalDate, cpiRate, config });

// ── Granny flat ──────────────────────────────────────────────────────────────────────────────
export const runGrannyFlatProjections = (simulatedFactFind: DraftFactFind, options: Record<string, unknown> = {}) =>
  postCalc('runGrannyFlatProjections', { simulatedFactFind, options });

export const getGrannyFlatBeforeAfter = (baseFactFind: DraftFactFind, gf: Record<string, unknown>) => postCalc('getGrannyFlatBeforeAfter', { baseFactFind, gf });

export const buildSimulatedFactFindForGrannyFlat = (baseFactFind: DraftFactFind, gf: Record<string, unknown> = {}) =>
  postCalc('buildSimulatedFactFindForGrannyFlat', { baseFactFind, gf });

export const getGrannyFlatDeprivationFromFactFind = (factFind: DraftFactFind, currentCombinedPartneredRate: number) =>
  postCalc('getGrannyFlatDeprivationFromFactFind', { factFind, currentCombinedPartneredRate });

// Suggestion-engine composite: evaluates every candidate (from generateGrannyFlatCandidates, which
// stays local) against the engine in ONE request instead of one request per candidate.
export const evaluateGrannyFlatCandidates = (
    factFind: unknown,
    candidates: unknown,
    legacyYears: number,
    grannyFlatDate?: string,
    /** The calendar the rows are cut on; the deep dive passes 'calendar_year' with ONE candidate. */
    projectionPeriodMode?: 'financial_year' | 'calendar_year',
) =>
    postCalc('evaluateGrannyFlatCandidates', { factFind, candidates, legacyYears, ...(grannyFlatDate ? { grannyFlatDate } : {}), ...(projectionPeriodMode ? { projectionPeriodMode } : {}) }, COMPOSITE_ENGINE_TIMEOUT_MS);

// Suggestion-engine composite: evaluates every candidate (from generateDownsizerCandidates, which
// stays local) against the engine in ONE request instead of one request per candidate.
export const evaluateDownsizerCandidates = (
    factFind: unknown,
    candidates: unknown,
    legacyYears: number,
    downsizeDate?: string,
    /** The calendar the rows are cut on; the deep dive passes 'calendar_year' with ONE candidate. */
    projectionPeriodMode?: 'financial_year' | 'calendar_year',
) =>
    postCalc('evaluateDownsizerCandidates', { factFind, candidates, legacyYears, ...(downsizeDate ? { downsizeDate } : {}), ...(projectionPeriodMode ? { projectionPeriodMode } : {}) }, COMPOSITE_ENGINE_TIMEOUT_MS);

// Suggestion-engine composite: evaluates every candidate (from generateRetirementVillageCandidates,
// which stays local) against the engine in ONE request instead of one request per candidate.
export const evaluateRetirementVillageCandidates = (factFind: unknown, villageInputs: unknown, candidates: unknown, legacyYears: number, villageEntryDate?: string) =>
    postCalc('evaluateRetirementVillageCandidates', { factFind, villageInputs, candidates, legacyYears, ...(villageEntryDate ? { villageEntryDate } : {}) }, COMPOSITE_ENGINE_TIMEOUT_MS);

// Single non-looped call producing { baselineFinancials, baselineContributions, baselineProjections }
// — needed both by analyzeBaselineOpportunities (for its own client-side-only problemStatement
// analysis) and as an input to evaluateSupportAtHomeStrategies below.
export const computeSupportAtHomeBaselineData = (factFind: DraftFactFind, servicePlan: Record<string, unknown>, legacyYears: number, evaluationOptions: Record<string, unknown>) =>
  postCalc('computeSupportAtHomeBaselineData', { factFind, servicePlan, legacyYears, evaluationOptions }, COMPOSITE_ENGINE_TIMEOUT_MS);

// Suggestion-engine composite: evaluates every candidate (from generateAllStrategies, which stays
// local) against the engine in ONE request instead of one request per candidate. Conventionally,
// candidates[0] is { type: 'doNothing', name: 'Do Nothing' } so the baseline comparison card comes
// back in the same request instead of a separate evaluateDoNothingBaseline round trip.
export const evaluateSupportAtHomeStrategies = (
  factFind: DraftFactFind,
  baselineData: Record<string, unknown>,
  servicePlan: Record<string, unknown>,
  legacyYears: number,
  candidates: Record<string, unknown>[],
  evaluationOptions: Record<string, unknown>,
) => postCalc('evaluateSupportAtHomeStrategies', { factFind, baselineData, servicePlan, legacyYears, candidates, evaluationOptions }, COMPOSITE_ENGINE_TIMEOUT_MS);

// Suggestion-engine composite: evaluates every RAD-funding/downsize scenario (from generateScenarios,
// which stays local) against the engine in ONE request instead of one request per scenario (up to
// ~150-300 scenarios previously).
// `careStartDate` (YYYY-MM-DD) is a property of the CASE, not of a candidate: every scenario is
// projected against the same entry date, or none of them can be compared. Omitted = care already
// under way, which is what every evaluation assumed before the parameter existed.
export const evaluateAgedCareRadFundingScenarios = (
  factFind: DraftFactFind,
  agedCareCosts: AgedCareCosts,
  scenarios: Record<string, unknown>[],
  legacyYears: number,
  careStartDate?: string,
  radDrawdown?: StrategyOptions['radDrawdown'],
  /** `'2025'` = a no-worse-off resident opting in to the 1 November 2025 fee arrangements; a fact about the case, like the entry date. */
  residentialFeeArrangements?: StrategyOptions['residentialFeeArrangements'],
  /**
   * The calendar the candidates' rows are cut on. Omit for the ranking run.
   *
   * The deep dive passes 'calendar_year' with ONE scenario in the array to re-cut that candidate:
   * the fact-find a candidate is projected from is derived server-side and never sent here, so the
   * re-run has to go back through the engine rather than be rebuilt on this side (owner, 23 Sep 2026).
   */
  projectionPeriodMode?: 'financial_year' | 'calendar_year',
  /** The couple's second room, where both are entering care (see the engine's `SecondRoomInput`). */
  secondRoom?: { person: 'client' | 'partner'; roomPrice: number; careStartDate?: string; extraServiceAnnual?: number } | null,
  /** A change of aged care home for someone already in care (see the engine's `FacilityMoveInput`); every candidate but "stay put" moves them. */
  facilityMove?: { person: 'client' | 'partner'; moveDate: string; newRoomPrice: number; extraServiceAnnual?: number; refundToday: number; currentRoomPrice: number; currentDapAnnual: number; grandfathered: boolean } | null,
  /** Leaving a retirement village for care (see the engine's `VillageExitInput`): the exit entitlement pays the room when it arrives. */
  villageExit?: { person: 'client' | 'partner'; leaveDate: string; refundDate: string; entitlement: number; paidAtMove?: boolean; feesAfterLeaving?: { perDay: number; fullDays: number; sharedDays: number; sharePct: number; total: number } } | null,
) =>
  postCalc(
    'evaluateAgedCareRadFundingScenarios',
    {
      factFind,
      agedCareCosts,
      scenarios,
      legacyYears,
      ...(careStartDate ? { careStartDate } : {}),
      ...(radDrawdown ? { radDrawdown } : {}),
      ...(residentialFeeArrangements ? { residentialFeeArrangements } : {}),
      ...(projectionPeriodMode ? { projectionPeriodMode } : {}),
      ...(secondRoom && secondRoom.roomPrice > 0 ? { secondRoom } : {}),
      ...(facilityMove && facilityMove.newRoomPrice > 0 ? { facilityMove } : {}),
      ...(villageExit && villageExit.entitlement > 0 ? { villageExit } : {}),
    },
    COMPOSITE_ENGINE_TIMEOUT_MS,
  );

// ── Downsizer contribution ──────────────────────────────────────────────────────────────────
// NOTE: the original local function mutated `assets` in place and returned nothing. The server
// route can't do that over HTTP, so it returns the mutated `assets` object — callers must replace
// their local `assets` reference with the returned value instead of reading the object they passed in.
export const applyDownsizerContributionsToAssets = (assets: DraftAssetMap, factFind: DraftFactFind, details: Record<string, unknown> = {}, options: Record<string, unknown> = {}) =>
  postCalc('applyDownsizerContributionsToAssets', { assets, factFind, details, options });

export const isDownsizerContributionEligible = (factFind: DraftFactFind, ownership: string = 'client') =>
  postCalc('isDownsizerContributionEligible', { factFind, ownership });

export const canCommenceAbpFromDownsizer = (factFind: DraftFactFind, ownership: string = 'client') =>
  postCalc('canCommenceAbpFromDownsizer', { factFind, ownership });

// ── Aged care downsize strategy ──────────────────────────────────────────────────────────────
export const applyAgedCareDownsizeToFactFind = (factFind: DraftFactFind, strategyOptions: StrategyOptions = {}) =>
  postCalc('applyAgedCareDownsizeToFactFind', { factFind, strategyOptions });

// ── The position once a saved plan is in place (the Action Plan's after-the-plan forms) ──────
export const agedCareScenarioPositionFactFind = (factFind: DraftFactFind, scenario: Record<string, unknown>) =>
  postCalc('agedCareScenarioPositionFactFind', { factFind, scenario });

export const cashflowPensionCandidatePositionFactFind = (factFind: DraftFactFind, candidate: Record<string, unknown>) =>
  postCalc('cashflowPensionCandidatePositionFactFind', { factFind, candidate });

// ── Residual deployment ──────────────────────────────────────────────────────────────────────
export const describeResidualDeployment = (deployment: Record<string, unknown>) => postCalc('describeResidualDeployment', { deployment });

// NOTE: the original local function mutated `data.assets` in place AND returned the $ deployed.
// The server route returns { data, deployed } — callers must replace their local `data` reference
// with the returned `data` (in addition to reading `deployed`).
export const applyResidualDeployment = (data: DraftFactFind, deployment: Record<string, unknown>, ctx: Record<string, unknown> = {}) =>
  postCalc('applyResidualDeployment', { data, deployment, ctx });

// ── Loans / gifting / dates ──────────────────────────────────────────────────────────────────
export const calculateLoanRepayment = (balance: number, annualRate: number, termYears: number, frequency: 'weekly' | 'fortnightly' | 'monthly' = 'monthly', loanType: 'pi' | 'io' = 'pi') =>
  postCalc('calculateLoanRepayment', { balance, annualRate, termYears, frequency, loanType });

export const calculateMarginalTaxRate = (assessableIncome: number) => postCalc('calculateMarginalTaxRate', { assessableIncome });

export const calculateDeprivedAssetsFromGifts = (giftsHistory: FactFindGiftsHistory, options: Record<string, unknown> = {}) =>
  postCalc('calculateDeprivedAssetsFromGifts', { giftsHistory, options });

// NOTE: the original local function returns a Map. The route can't send a Map over JSON, so it
// sends [fy, amount] pairs — this wrapper reconstructs the Map so callers see the same shape.
export const buildGiftsByFinancialYear = async (giftsHistory: FactFindGiftsHistory, strategyGiftsByFyEnd: Record<string, number> = {}): Promise<Map<string, number>> =>
  new Map(await postCalc('buildGiftsByFinancialYear', { giftsHistory, strategyGiftsByFyEnd }));

export const getAnnualMinimumAbpDraw = (balance: number, ownerAge: number) => postCalc('getAnnualMinimumAbpDraw', { balance, ownerAge });

export const getAbpProjectionNominatedAnnual = (asset: DraftAsset) => postCalc('getAbpProjectionNominatedAnnual', { asset });

// NOTE: the original local function mutated `asset` in place (setting __abpFundedAcquisition/income)
// and returned nothing. The server route returns the mutated `asset` — callers must replace their
// local `asset` reference with the returned value.
export const markAbpFundedAcquisitionAfterLiquidation = (asset: DraftAsset, factFind: DraftFactFind, yearIndex = 1) =>
  postCalc('markAbpFundedAcquisitionAfterLiquidation', { asset, factFind, yearIndex });

// NOTE: the original local function mutated `factFind` in place (initializing each ABP asset's
// __abpProjectionNominatedAnnual) and returned nothing. The server route returns the mutated
// `factFind` — callers must replace their local `factFind` reference with the returned value.
export const initializeAbpProjectionNomineesOnFactFind = (factFind: DraftFactFind) =>
  postCalc('initializeAbpProjectionNomineesOnFactFind', { factFind });

export const getAustralianFYEndingYearContainingDate = (date: Date | string) =>
  postCalc('getAustralianFYEndingYearContainingDate', { date });
